import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Field } from "./Contact";
import { QuoteAttachments } from "./QuoteAttachments";
import { useT, useLang } from "@/i18n";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORK_TYPES = ["Ponte o passerella", "Scuola", "Palestra o palazzetto", "Piscina", "Auditorium, teatro o museo", "Copertura di edificio pubblico", "Altro"];
const PHASES = ["Idea o studio di fattibilità", "Progetto in corso", "Gara bandita o in preparazione", "Lavori aggiudicati"];
const EMPTY = { company: "", name: "", role: "", email: "", phone: "", location: "", work_type: "", phase: "", surface: "", message: "" };

const Choice = ({ label, value, onChange, items, prefix, testId, t }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-[0.6rem] h-auto w-full rounded-none border-0 border-b border-[#F7F5F0]/20 bg-transparent px-0 py-[0.7rem] text-base text-[#F7F5F0] shadow-none focus:border-[#D98A52] focus:ring-0 focus:ring-offset-0" data-testid={testId}>
        <SelectValue placeholder={t("form.select", "Seleziona")} />
      </SelectTrigger>
      <SelectContent className="border-white/10 bg-[#1B1816] text-[#F7F5F0]">
        {items.map((it, i) => <SelectItem key={it} value={it} data-testid={`${testId}-${i}`}>{t(`${prefix}.${i}`, it)}</SelectItem>)}
      </SelectContent>
    </Select>
  </label>
);

export const PublicBodyForm = () => {
  const t = useT();
  const { lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const uploading = files.some((f) => !f.id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.work_type || !form.phase) return toast.error(t("pa.form.errSel", "Seleziona tipo di opera e fase"));
    if (uploading) return toast.error(t("att.wait", "Attendi la fine del caricamento degli allegati"));
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, project_type: "Opera pubblica", source: "ente", lang, attachment_ids: files.map((f) => f.id) });
      track("richiesta_ente_pubblico", { tipo_opera: form.work_type, fase: form.phase, lingua: lang });
      toast.success(t("pa.form.ok", "Richiesta inviata. Vi ricontattiamo entro 48 ore lavorative."));
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.status === 429 ? t("form.err429", "Troppe richieste, riprova più tardi.") : t("form.err", "Invio non riuscito. Controlla i campi e riprova."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-8 sm:grid-cols-2" data-testid="public-form">
      <Field label={t("pa.form.company", "Ente / Comune *")} id="pa-company" required minLength={2} value={form.company} onChange={set("company")} />
      <Field label={t("pa.form.name", "Referente *")} id="pa-name" required minLength={2} value={form.name} onChange={set("name")} />
      <Field label={t("pa.form.role", "Ruolo (es. RUP, ufficio tecnico)")} id="pa-role" value={form.role} onChange={set("role")} />
      <Field label={t("pa.form.location", "Comune / località dell'opera")} id="pa-location" value={form.location} onChange={set("location")} />
      <Field label="Email *" id="pa-email" type="email" required value={form.email} onChange={set("email")} />
      <Field label={t("form.phone", "Telefono")} id="pa-phone" value={form.phone} onChange={set("phone")} />
      <Choice label={t("pa.form.work", "Tipo di opera *")} value={form.work_type} onChange={(v) => setForm({ ...form, work_type: v })} items={WORK_TYPES} prefix="pa.w" testId="pa-work-type" t={t} />
      <Choice label={t("pa.form.phase", "Fase *")} value={form.phase} onChange={(v) => setForm({ ...form, phase: v })} items={PHASES} prefix="pa.ph" testId="pa-phase" t={t} />
      <div className="sm:col-span-2">
        <Field label={t("pa.form.message", "Descrivi l'opera (luce, dimensioni, tempi) *")} id="pa-message" textarea required minLength={5} value={form.message} onChange={set("message")} />
      </div>
      <QuoteAttachments files={files} setFiles={setFiles} />
      <div className="flex flex-wrap items-center justify-between gap-6 sm:col-span-2">
        <p className="max-w-sm text-xs text-[#A39E93]">{t("form.privacy", "Inviando accetti il trattamento dei dati per la gestione della richiesta.")}</p>
        <button type="submit" disabled={loading || uploading} className="btn-amber disabled:opacity-60" data-testid="public-form-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t("pa.form.submit", "Invia la richiesta")} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
};
